import type { ConsignmentLineItem, PalletSummary } from './types';
import type { DerivedPallet } from './pallet-types';

/**
 * Groups consignment line ids by the pallet UUID they are assigned to.
 * A line on several pallets contributes its id to each of them.
 */
function groupLineIdsByPalletUuid(lines: ConsignmentLineItem[]): Map<string, string[]> {
  const byUuid = new Map<string, string[]>();
  for (const line of lines) {
    for (const uuid of line.palletIds ?? []) {
      if (!uuid) continue;
      const ids = byUuid.get(uuid) ?? [];
      ids.push(line.id);
      byUuid.set(uuid, ids);
    }
  }
  return byUuid;
}

/**
 * Builds the consignment's pallets from the lines endpoint response.
 *
 * Prefers the authoritative `pallets` array (which includes empty pallets and
 * carries type/capacity metadata), attaching each line via its `palletIds`.
 * Falls back to deriving pallets purely from the lines when the backend does
 * not return a `pallets` array (older versions).
 *
 * @param pallets - The `pallets` array from the lines response (may be missing)
 * @param lines - Consignment line items returned by `GET /lines`
 */
export function buildConsignmentPallets(
  pallets: PalletSummary[] | null | undefined,
  lines: ConsignmentLineItem[]
): DerivedPallet[] {
  if (!pallets || pallets.length === 0) {
    return derivePalletsFromLines(lines);
  }

  const lineIdsByUuid = groupLineIdsByPalletUuid(lines);

  return pallets.map((pallet) => ({
    id: pallet.uuid,
    palletNumber: pallet.name,
    type: pallet.type ?? null,
    maxWeight: pallet.maxWeight ?? null,
    maxVolume: pallet.maxVolume ?? null,
    currentWeight: pallet.currentWeight ?? null,
    currentVolume: pallet.currentVolume ?? null,
    unitCount: pallet.unitCount ?? null,
    assignedLineIds: lineIdsByUuid.get(pallet.uuid) ?? []
  }));
}

/**
 * Derives the list of pallets for a consignment from its line items.
 *
 * The pallet management API (see `pallet-view.md`) does NOT expose a dedicated
 * `GET /pallets` endpoint. Instead, every consignment line carries the pallets
 * it is assigned to via the index-aligned `palletIds` (full UUIDs) and
 * `palletNames` (short 8-char names) arrays. A line can belong to several
 * pallets, and an unassigned line has `null` for both fields.
 *
 * This function inverts that relationship: it groups line ids by pallet, so the
 * UI can render pallet groups without an extra request.
 *
 * @param lines - Consignment line items returned by `GET /lines`
 * @returns Pallets with their assigned line ids, in first-seen order
 */
export function derivePalletsFromLines(lines: ConsignmentLineItem[]): DerivedPallet[] {
  const palletsById = new Map<string, DerivedPallet>();

  for (const line of lines) {
    const ids = line.palletIds ?? [];
    const names = line.palletNames ?? [];

    ids.forEach((palletId, i) => {
      if (!palletId) return;

      let pallet = palletsById.get(palletId);
      if (!pallet) {
        pallet = {
          id: palletId,
          // Fall back to the first 8 chars of the UUID if names are missing/misaligned
          palletNumber: names[i] ?? palletId.slice(0, 8),
          assignedLineIds: []
        };
        palletsById.set(palletId, pallet);
      }

      pallet.assignedLineIds.push(line.id);
    });
  }

  return Array.from(palletsById.values());
}
